import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';

/* Copy dos COMPONENTES da trilha de auditoria (ADR-0708): toolbar de filtros e a tabela. O
   header/paginação da PÁGINA ficam em `content/sections/audit.ts` (frente de routes/) — este
   arquivo é só o que `components/audit/` desenha por conta própria.

   Os rótulos de `action`/`targetType` NÃO estão aqui: vivem em `audit-action-catalog.ts` com um
   parâmetro `locale?` opcional, porque `auditTargetTypeLabel` também é consumido por
   `routes/audit-page.tsx` (mesma razão de `pack-catalog-labels.ts`/`releases-error.ts`). */
interface AuditTrailContent {
  readonly filters: {
    readonly searchAriaLabel: string;
    readonly searchPlaceholder: string;
    readonly actorSearchAriaLabel: string;
    readonly actorSearchPlaceholder: string;
    readonly targetTypeAriaLabel: string;
    readonly targetTypePlaceholder: string;
    readonly allTargetTypes: string;
    readonly actionAriaLabel: string;
    readonly actionPlaceholder: string;
    readonly allActions: string;
    readonly periodAriaLabel: string;
    readonly periodAll: string;
    readonly period24h: string;
    readonly period7d: string;
    readonly period30d: string;
  };
  readonly table: {
    readonly emptyFilteredTitle: string;
    readonly emptyFilteredDescription: string;
    readonly clearFilters: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly columnWhen: string;
    readonly columnActor: string;
    readonly columnAction: string;
    readonly columnTarget: string;
    readonly columnDetail: string;
  };
}

const AUDIT_TRAIL_CONTENT: Bilingual<AuditTrailContent> = {
  'pt-BR': {
    filters: {
      searchAriaLabel: 'Buscar por ação ou ID do alvo',
      searchPlaceholder: 'Buscar ação ou alvo…',
      actorSearchAriaLabel: 'Filtrar por ID do operador',
      actorSearchPlaceholder: 'ID do operador…',
      targetTypeAriaLabel: 'Filtrar por tipo de alvo',
      targetTypePlaceholder: 'Tipo de alvo',
      allTargetTypes: 'Todos os tipos',
      actionAriaLabel: 'Filtrar por ação',
      actionPlaceholder: 'Ação',
      allActions: 'Todas as ações',
      periodAriaLabel: 'Período',
      periodAll: 'Todos',
      period24h: '24h',
      period7d: '7 dias',
      period30d: '30 dias',
    },
    table: {
      emptyFilteredTitle: 'Nenhum resultado',
      emptyFilteredDescription: 'Nenhuma ação corresponde à busca ou aos filtros.',
      clearFilters: 'Limpar filtros',
      emptyTitle: 'Trilha vazia',
      emptyDescription: 'As ações administrativas registradas aparecem aqui.',
      columnWhen: 'Quando',
      columnActor: 'Ator',
      columnAction: 'Ação',
      columnTarget: 'Alvo',
      columnDetail: 'Detalhe',
    },
  },
  'en-US': {
    filters: {
      searchAriaLabel: 'Search by action or target ID',
      searchPlaceholder: 'Search action or target…',
      actorSearchAriaLabel: 'Filter by operator ID',
      actorSearchPlaceholder: 'Operator ID…',
      targetTypeAriaLabel: 'Filter by target type',
      targetTypePlaceholder: 'Target type',
      allTargetTypes: 'All types',
      actionAriaLabel: 'Filter by action',
      actionPlaceholder: 'Action',
      allActions: 'All actions',
      periodAriaLabel: 'Period',
      periodAll: 'All',
      period24h: '24h',
      period7d: '7 days',
      period30d: '30 days',
    },
    table: {
      emptyFilteredTitle: 'No results',
      emptyFilteredDescription: 'No action matches the search or filters.',
      clearFilters: 'Clear filters',
      emptyTitle: 'Empty trail',
      emptyDescription: 'Recorded administrative actions show up here.',
      columnWhen: 'When',
      columnActor: 'Actor',
      columnAction: 'Action',
      columnTarget: 'Target',
      columnDetail: 'Detail',
    },
  },
};

export const useAuditTrailContent = (): AuditTrailContent => useBilingual(AUDIT_TRAIL_CONTENT);
